import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { BookMarked, Link as LinkIcon, StickyNote, ExternalLink, Search, X } from "lucide-react";

export default function StudentMaterials() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/materials").then((r) => setItems(r.data));
  }, []);

  const filtered = items.filter(
    (m) =>
      m.title.toLowerCase().includes(q.toLowerCase()) ||
      (m.subject || "").toLowerCase().includes(q.toLowerCase()) ||
      (m.description || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AppShell title="Study materials" subtitle="Curated notes and links from your teachers.">
      <div className="mb-6 relative max-w-md">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title, subject or description…"
          className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
          data-testid="material-search-input"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="student-material-list">
        {filtered.map((m) => (
          <div key={m.id} className="qs-card p-5" data-testid={`material-card-${m.id}`}>
            <div className={`w-11 h-11 rounded-xl grid place-items-center ${m.content_type === "link" ? "bg-sky-50 text-sky-600" : "bg-amber-50 text-amber-600"}`}>
              {m.content_type === "link" ? <LinkIcon className="w-5 h-5" /> : <StickyNote className="w-5 h-5" />}
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{m.title}</h3>
            <p className="text-xs text-slate-500 mt-1">
              {m.subject} · from {m.created_by_name || "Teacher"}
            </p>
            <p className="mt-2 text-sm text-slate-600 line-clamp-3">{m.description || "—"}</p>
            <div className="mt-4">
              {m.content_type === "link" ? (
                <a href={m.url} target="_blank" rel="noreferrer" className="btn-primary text-xs" data-testid={`material-open-${m.id}`}>
                  Open link <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : (
                <button onClick={() => setExpanded(m)} className="btn-primary text-xs" data-testid={`material-open-${m.id}`}>
                  Read note <BookMarked className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full text-center text-slate-500 py-16" data-testid="no-materials">
            No study materials yet.
          </p>
        )}
      </div>

      {expanded && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setExpanded(null)} data-testid="material-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-indigo-600">{expanded.subject}</p>
                <h3 className="font-display text-xl font-bold text-slate-900 mt-1">{expanded.title}</h3>
                <p className="text-xs text-slate-500 mt-1">from {expanded.created_by_name || "Teacher"}</p>
              </div>
              <button onClick={() => setExpanded(null)} className="text-slate-400 hover:text-slate-800" data-testid="material-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-auto text-slate-700 whitespace-pre-wrap">{expanded.body}</div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
